<!--app\javascript\dashboard\components\widgets\conversation\WhatsappTemplates\TemplatesPicker.vue-->

<script>
export default {
  props: {
    inboxId: {
      type: Number,
      default: undefined,
    },
  },
  emits: ['onSelect'],
  data() {
    return {
      query: '',
      expandedTemplates: [],
    };
  },
  computed: {
    accountId() {
      return this.$route?.params?.accountId;
    },
    whatsAppTemplateMessages() {
      return this.$store.getters['inboxes/getWhatsAppTemplates'](this.inboxId)
        .filter(template => template.status.toLowerCase() === 'approved');
    },
    filteredTemplateMessages() {
      return this.whatsAppTemplateMessages.filter(template =>
        template.name.toLowerCase().includes(this.query.toLowerCase())
      );
    },
  },
  methods: {
    toggleExpand(templateId) {
      const index = this.expandedTemplates.indexOf(templateId);
      if (index > -1) {
        this.expandedTemplates.splice(index, 1);
      } else {
        this.expandedTemplates.push(templateId);
      }
    },
    isExpanded(templateId) {
      return this.expandedTemplates.includes(templateId);
    },
    truncateTitle(name) {
      if (name.length > 50) {
        return name.substring(0, 50) + '...';
      }
      return name;
    },
    getTemplatebody(template) {
      return template.components.find(component => component.type === 'BODY')
        .text;
    },
    getTemplateHeaderFormat(template) {
      const header = template.components.find(
        component => component.type === 'HEADER'
      );
      return header?.format || null;
    },
    getMediaIcon(format) {
      const icons = {
        IMAGE: 'i-lucide-image',
        VIDEO: 'i-lucide-video',
        DOCUMENT: 'i-lucide-file-text',
      };
      return icons[format] || null;
    },
    getMediaLabel(format) {
      const labels = {
        IMAGE: 'Imagem',
        VIDEO: 'Vídeo',
        DOCUMENT: 'Documento',
      };
      return labels[format] || null;
    },
    openTemplateEditor(template) {
      if (!template?.id || !this.inboxId) {
        return;
      }

      const accountId = this.accountId;
      if (!accountId) {
        console.warn('Account ID not found for WhatsApp template editor navigation');
        return;
      }

      this.$router.push({
        name: 'settings_whatsapp_template_edit',
        params: {
          accountId,
          templateId: template.id,
        },
        query: {
          inboxId: this.inboxId.toString(),
        },
      });
    },
  },
};
</script>

<template>
  <div class="w-full">
    <div class="gap-1 templates__list-search">
      <fluent-icon icon="search" class="search-icon" size="16" />
      <input
        v-model="query"
        type="search"
        :placeholder="$t('WHATSAPP_TEMPLATES.PICKER.SEARCH_PLACEHOLDER')"
        class="templates__search-input"
      />
    </div>
    <div class="template__list-container">
      <div v-for="(template, i) in filteredTemplateMessages" :key="template.id" class="template__card">
        <!-- Header (sempre visível) -->
        <div class="template__card-header" @click="toggleExpand(template.id)">
          <div class="flex items-center gap-3 flex-1 min-w-0">
            <p class="label-title truncate" :title="template.name">
              {{ truncateTitle(template.name) }}
            </p>
          </div>
          <div class="flex items-center gap-2 flex-shrink-0">
            <span
              v-if="getTemplateHeaderFormat(template)"
              class="template__badge template__badge--media"
            >
              <i :class="getMediaIcon(getTemplateHeaderFormat(template))" />
              {{ getMediaLabel(getTemplateHeaderFormat(template)) }}
            </span>
            <span class="template__badge">
              {{ template.language }}
            </span>
            <span class="template__badge">
              {{ template.category }}
            </span>
            <i
              class="i-lucide-chevron-down transition-transform duration-300 text-n-slate-11"
              :class="{ 'rotate-180': isExpanded(template.id) }"
            />
          </div>
        </div>

        <!-- Body (expansível) -->
        <transition name="expand">
          <div v-show="isExpanded(template.id)" class="template__card-body">
            <div class="template__content">
              <p class="content-label">
                {{ $t('WHATSAPP_TEMPLATES.PICKER.LABELS.TEMPLATE_BODY') }}
              </p>
              <pre class="content-preview">{{ getTemplatebody(template) }}</pre>
            </div>
            <div class="template__actions">
              <button
                type="button"
                class="action-button action-button--select"
                @click="$emit('onSelect', template)"
              >
                <i class="i-lucide-check" />
                Selecionar Template
              </button>
              <button
                type="button"
                class="action-button action-button--edit"
                @click="openTemplateEditor(template)"
              >
                <i class="i-lucide-pencil" />
                {{ $t('WHATSAPP_TEMPLATES.PICKER.EDIT_TEMPLATE') }}
              </button>
            </div>
          </div>
        </transition>

        <hr v-if="i != filteredTemplateMessages.length - 1" :key="`hr-${i}`" />
      </div>
      <div v-if="!filteredTemplateMessages.length">
        <p>
          {{ $t('WHATSAPP_TEMPLATES.PICKER.NO_TEMPLATES_FOUND') }}
          <strong>{{ query }}</strong>
        </p>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.templates__list-search {
  @apply items-center flex bg-n-alpha-2 dark:bg-n-solid-1 mb-2.5 py-0 px-2.5 rounded-lg border border-n-weak;

  .search-icon {
    @apply text-n-slate-11;
  }

  .templates__search-input {
    @apply bg-transparent border-0 text-sm h-10 m-0 text-n-slate-12 placeholder:text-n-slate-10;
  }
}

.template__list-container {
  @apply bg-n-alpha-2 dark:bg-n-solid-1 rounded-lg max-h-[500px] overflow-y-auto p-2;
}

.template__card {
  @apply border border-n-weak rounded-lg mb-2 transition-all duration-200 hover:border-n-iris-6 bg-white dark:bg-n-slate-1;

  &:last-child {
    @apply mb-0;
  }
}

.template__card-header {
  @apply flex items-center justify-between p-3 cursor-pointer select-none;

  .label-title {
    @apply text-sm font-semibold text-n-slate-12;
  }
}

.template__badge {
  @apply inline-block px-2 py-1 text-xs font-medium bg-n-slate-3 dark:bg-n-slate-2 text-n-slate-11 rounded;
}

.template__badge--media {
  @apply inline-flex items-center gap-1 bg-n-iris-2 dark:bg-n-iris-3 text-n-iris-11 dark:text-n-iris-11;

  i {
    @apply text-xs;
  }
}

.template__card-body {
  @apply border-t border-n-weak px-3 pb-3;
}

.template__content {
  @apply pt-3 pb-3;

  .content-label {
    @apply text-xs font-semibold text-n-slate-10 mb-2;
  }

  .content-preview {
    @apply text-sm text-n-slate-12 bg-n-alpha-2 dark:bg-n-solid-2 p-3 rounded-md border border-n-weak whitespace-pre-wrap;
    font-family: monospace;
  }
}

.template__actions {
  @apply flex gap-2;
}

.action-button {
  @apply inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 border;

  i {
    @apply text-base;
  }
}

.action-button--select {
  @apply flex-1 bg-n-iris-9 text-white border-n-iris-9 hover:bg-n-iris-10;
}

.action-button--edit {
  @apply bg-transparent text-n-iris-9 border-n-iris-6 hover:bg-n-iris-2;
}

hr {
  @apply hidden;
}

/* Transition for expand/collapse */
.expand-enter-active,
.expand-leave-active {
  transition: all 0.3s ease;
  overflow: hidden;
}

.expand-enter-from,
.expand-leave-to {
  max-height: 0;
  opacity: 0;
  padding-top: 0;
  padding-bottom: 0;
}

.expand-enter-to,
.expand-leave-from {
  max-height: 500px;
  opacity: 1;
}
</style>
